import React, { useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  Lock,
  ChevronRight,
  CoreBundle,
  IcoLayers,
} from "./components/Icons.js";
import CoreBundleView from "./CoreBundle.js";
import useLoader from "../../../../hooks/useLoader.js";
import { useSnackbar } from "notistack";
import BundleService from "../../../../services/BundleService.js";

const NAV_CORE_ALL = {
  view: "coreBundle",
  initialSection: "core",
  initialSource: "All",
};
const NAV_CORE_CLAIMANT = {
  view: "coreBundle",
  initialSection: "core",
  initialSource: "Claimant",
};
const NAV_CORE_RESPONDENT = {
  view: "coreBundle",
  initialSection: "core",
  initialSource: "Respondent",
};
const NAV_CORE_NEUTRAL = {
  view: "coreBundle",
  initialSection: "core",
  initialSource: "Neutral",
};
const NAV_TRIAL = {
  view: "coreBundle",
  initialSection: "trial",
  initialSource: "All",
};
const NAV_PRIVATE = {
  view: "coreBundle",
  initialSection: "private",
  initialSource: "All",
};
const isPrivateBundle = (bundle) =>
  (bundle?.visibilityType || "me") === "strictMode";

const DocumentsLanding = ({
  caseObj,
  onCreateBundle,
  id,
  caseDetails,
  isActionsDisabled,
  viewOnly,
  view,
  setView,
  trigger,
  setTrigger,
}) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [bundleNav, setBundleNav] = useState({
    initialSection: "core",
    initialSource: "All",
  });

  const [trialBundles, setTrialBundles] = useState([]);
  const prevViewRef = React.useRef(view);

  const fetchBundles = useCallback(async () => {
    try {
      setLoader({ state: true, message: "Fetching bundles..." });

      const response = await BundleService.getBundleDocuments(`?caseId=${id}`);

      let bundles = [];
      if (Array.isArray(response)) {
        bundles = response;
      } else if (response?.data) {
        bundles = Array.isArray(response.data) ? response.data : [];
      } else if (response?.bundles) {
        bundles = Array.isArray(response.bundles) ? response.bundles : [];
      }

      setTrialBundles(bundles);
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to fetch bundles", {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      if (typeof setTrigger === "function") {
        setTrigger(false);
      }
    }
  }, [id, enqueueSnackbar, setLoader, setTrigger]);

  useEffect(() => {
    if (id && (trialBundles.length === 0 || trigger)) {
      fetchBundles();
    }
  }, [id, trigger, trialBundles.length, fetchBundles]);

  useEffect(() => {
    if (view === "landing" && prevViewRef.current !== "landing") {
      fetchBundles();
    }
    prevViewRef.current = view;
  }, [view, fetchBundles]);

  const recentBundles = React.useMemo(() => {
    return [...trialBundles]
      .sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (dateA !== dateB) return dateB - dateA;

        const codeA =
          parseInt((a.bundleCode || "").replace(/\D/g, ""), 10) || a.id || 0;
        const codeB =
          parseInt((b.bundleCode || "").replace(/\D/g, ""), 10) || b.id || 0;
        return codeB - codeA;
      })
      .slice(0, 2);
  }, [trialBundles]);

  const privateBundles = React.useMemo(
    () => trialBundles.filter(isPrivateBundle),
    [trialBundles],
  );

  const navigateTo = ({ view: targetView, initialSection, initialSource }) => {
    setBundleNav({ initialSection, initialSource });
    setView(targetView);
  };

  if (view === "coreBundle") {
    return (
      <CoreBundleView
        onBack={() => setView("landing")}
        initialSection={bundleNav.initialSection}
        initialSource={bundleNav.initialSource}
        {...{ id, caseDetails, isActionsDisabled, viewOnly, setTrigger }}
      />
    );
  }

  return (
    <Wrap>
      <Header>
        <TitleSection>
          <Title>Documents & Bundles</Title>
          <Subtitle>
            The Core Bundle holds all case documents. Trial Bundles are curated
            compilations created for hearings.
          </Subtitle>
        </TitleSection>
      </Header>

      <CategoryGrid>
        <Card $accent="#00818a" onClick={() => navigateTo(NAV_CORE_ALL)}>
          <CardHeader>
            <CardHeaderLeft>
              <IconBubble $bg="#f0fdf9" $fg="#00818a">
                <CoreBundle size={19} />
              </IconBubble>
              <CardTitle>Core Bundle</CardTitle>
            </CardHeaderLeft>
            <Badge style={{ background: "#f3f4f6", color: "#374151" }}>
              Source
            </Badge>
          </CardHeader>
          <CardDescription>
            All case documents — Claimant, Respondent and Neutral.
          </CardDescription>
          <SubLinks onClick={(e) => e.stopPropagation()}>
            <SubLink
              $bg="#fef3c7"
              $color="#d97706"
              onClick={() => navigateTo(NAV_CORE_CLAIMANT)}
            >
              <DotSmall $color="#f59e0b" /> Claimant
            </SubLink>
            <SubLink
              $bg="#f1f5f9"
              $color="#475569"
              onClick={() => navigateTo(NAV_CORE_RESPONDENT)}
            >
              <DotSmall $color="#94a3b8" /> Respondent
            </SubLink>
            <SubLink
              $bg="#ccfbf1"
              $color="#0f766e"
              onClick={() => navigateTo(NAV_CORE_NEUTRAL)}
            >
              <DotSmall $color="#14b8a6" /> Neutral
            </SubLink>
          </SubLinks>
          <CardFooter>
            <CountRow></CountRow>
            <OpenLink>
              Open all <ChevronRight size={14} />
            </OpenLink>
          </CardFooter>
        </Card>

        <Card $accent="#00818a" onClick={() => navigateTo(NAV_TRIAL)}>
          <CardHeader>
            <CardHeaderLeft>
              <IconBubble $bg="#e0f2f1" $fg="#00818a">
                <IcoLayers size={18} />
              </IconBubble>
              <CardTitle>Trial Bundles</CardTitle>
            </CardHeaderLeft>
            <Badge style={{ background: "#e0f2f1", color: "#00818a" }}>
              Curated
            </Badge>
          </CardHeader>
          <CardDescription>
            Curated bundles drawn from the Core Bundle for hearings.
          </CardDescription>

          <SubLinks onClick={(e) => e.stopPropagation()}>
            {recentBundles.length > 0 ? (
              recentBundles.map((bundle) => (
                <SubLink
                  key={bundle.id || bundle.bundleCode}
                  $bg="#e0f2f1"
                  $color="#00818a"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateTo(NAV_TRIAL);
                  }}
                >
                  {bundle.name}
                </SubLink>
              ))
            ) : (
              <SubLink
                $bg="#e0f2f1"
                $color="#00818a"
                onClick={() => navigateTo(NAV_TRIAL)}
              >
                No bundles yet
              </SubLink>
            )}
          </SubLinks>

          <CardFooter>
            <CountRow></CountRow>
            <OpenLink>
              Open bundles <ChevronRight size={14} />
            </OpenLink>
          </CardFooter>
        </Card>

        <Card $accent="#6d28d9" onClick={() => navigateTo(NAV_PRIVATE)}>
          <CardHeader>
            <CardHeaderLeft>
              <IconBubble $bg="#ede9fe" $fg="#6d28d9">
                <Lock size={18} />
              </IconBubble>
              <CardTitle>My Private Bundles</CardTitle>
            </CardHeaderLeft>
            <Badge style={{ background: "#ede9fe", color: "#6d28d9" }}>
              Yours only
            </Badge>
          </CardHeader>

          <CardDescription>
            Private copies of bundles or documents — visible only to you.
          </CardDescription>

          <CardFooter>
            <CountRow $color="#6d28d9">
              {privateBundles.length} private
            </CountRow>
            <OpenLink $color="#6d28d9">
              Open private <ChevronRight size={14} />
            </OpenLink>
          </CardFooter>
        </Card>
      </CategoryGrid>

      <RecentSection>
        <SectionHeader>
          <SectionLabel>Recent Trial Bundles</SectionLabel>
          <SectionCount>
            {trialBundles.length} in this case · {privateBundles.length} private
          </SectionCount>
        </SectionHeader>

        <BundleList>
          {recentBundles.length > 0 ? (
            recentBundles.map((bundle) => (
              <BundleRow
                key={bundle.id || bundle.bundleCode}
                onClick={() => navigateTo(NAV_TRIAL)}
              >
                {/* <BundleIconWrap>
                  <FolderArchive size={16} />
                </BundleIconWrap> */}
                <BundleInfo>
                  <BundleName>{bundle.name}</BundleName>
                  <BundleMeta>
                    {bundle.totalPages || 0} pages • Created{" "}
                    {bundle.created_at
                      ? new Date(bundle.created_at).toLocaleDateString()
                      : "Recently"}
                  </BundleMeta>
                </BundleInfo>
                <BundleActions>
                  <TypeBadge>{bundle.bundleType || "Trial Bundle"}</TypeBadge>
                  <BundleOpenLink>
                    Open bundle <ChevronRight size={14} />
                  </BundleOpenLink>
                </BundleActions>
              </BundleRow>
            ))
          ) : (
            <EmptyRecent>No trial bundles created yet.</EmptyRecent>
          )}
        </BundleList>
      </RecentSection>
    </Wrap>
  );
};

export default DocumentsLanding;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

const Wrap = styled.div`
  padding: 28px 0;
  animation: ${fadeIn} 0.25s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 20px;
  font-family: ${({ theme }) =>
    theme?.fonts?.primaryFontSemiBold || "openSans-SemiBold, sans-serif"};
  font-weight: 700;
  color: #293461;
  margin: 0;
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  font-family: openSans-Regular, sans-serif;
  color: #6b7280;
  margin: 5px 0 0 0;
  line-height: 1.5;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 36px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 22px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $accent }) => $accent || "#00818a"};
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: transparent;

    &::before {
      opacity: 1;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Badge = styled.span`
  font-size: 11px;
  font-family: openSans-SemiBold, sans-serif;
  padding: 3px 9px;
  border-radius: 20px;
  font-weight: 600;
  letter-spacing: 0.2px;
`;

const IconBubble = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  flex-shrink: 0;
`;

const CardTitle = styled.h3`
  font-size: 15px;
  font-family: openSans-SemiBold, sans-serif;
  color: #293461;
  margin: 0 0 5px 0;
  font-weight: 700;
`;

const CardDescription = styled.p`
  font-size: 12.5px;
  font-family: openSans-Regular, sans-serif;
  color: #6b7280;
  line-height: 1.55;
  margin: 0 0 16px 0;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 14px;
  border-top: 1px solid #f3f4f6;
`;

const CountRow = styled.div`
  font-size: 12px;
  font-family: openSans-SemiBold, sans-serif;
  color: ${({ $color }) => $color || "#00818a"};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OpenLink = styled.div`
  font-size: 12px;
  font-family: openSans-SemiBold, sans-serif;
  color: ${({ $color }) => $color || "#00818a"};
  display: flex;
  align-items: center;
  gap: 3px;
  transition: gap 0.15s;

  ${Card}:hover & {
    gap: 6px;
  }
`;

const SubLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
`;

const SubLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-family: openSans-SemiBold, sans-serif;
  border-radius: 20px;
  border: 1.5px solid ${({ $color }) => $color}22;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  cursor: pointer;
  transition: all 0.15s;
  font-weight: 600;

  &:hover {
    filter: brightness(0.93);
    transform: translateY(-1px);
  }
`;

const DotSmall = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-block;
  flex-shrink: 0;
`;

const RecentSection = styled.div`
  margin-top: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-family: openSans-SemiBold, sans-serif;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #374151;
  font-weight: 700;
`;

const SectionCount = styled.span`
  font-size: 12.5px;
  color: #6b7280;
  font-family: openSans-Regular, sans-serif;
`;

const BundleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BundleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    background: #f8fafc;
    border-color: #d1d5db;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const BundleInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const BundleName = styled.div`
  font-size: 13.5px;
  font-family: openSans-SemiBold, sans-serif;
  color: #293461;
  font-weight: 600;
`;

const BundleMeta = styled.div`
  font-size: 12px;
  font-family: openSans-Regular, sans-serif;
  color: #9ca3af;
  margin-top: 2px;
`;

const BundleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const TypeBadge = styled.span`
  font-size: 11px;
  font-family: openSans-SemiBold, sans-serif;
  padding: 3px 9px;
  border-radius: 20px;
  background: #e0f2f1;
  color: #00818a;
  font-weight: 600;
`;

const BundleOpenLink = styled.div`
  font-size: 12px;
  font-family: openSans-SemiBold, sans-serif;
  color: #00818a;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
`;

const EmptyRecent = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-style: italic;
  background: #f8fafc;
  border-radius: 10px;
`;
